import React, { useMemo, useEffect, useState } from "react";
import {
    Dialog,
    DialogHeader,
    DialogBody,
    DialogFooter,
    Typography,
} from "@material-tailwind/react";
import { XMarkIcon } from "@heroicons/react/24/outline";
import { useForm } from "react-hook-form";
import * as yup from "yup";
import { yupResolver } from "@hookform/resolvers/yup";
import ShowLoader from "@/components/Loader";
import { useDispatch, useSelector } from "react-redux";
import { fetchPendingOrders } from "@/store/slices/orderSlice";
import { useTranslation } from "react-i18next";

export function RefundApprovalModal({
    isOpen,
    onClose,
    onConfirm,
    loading,
    payableAmount,
    amountPaid,
    userId,
}) {

    const dispatch = useDispatch();
    const { t } = useTranslation('order');

    const { pending_orders } = useSelector((state) => state.order);
    // const amountPaid = refundData?.order?.payment_summary?.paid_amount || 0;
    const [refundType, setRefundType] = useState("paid");
    const [adjustments, setAdjustments] = useState([]);
    const [adjustmentErrors, setAdjustmentErrors] = useState({});
    

    // const maxRefund = useMemo(() => {
    //     if (!payableAmount) return 0;
    //     return Number(String(payableAmount).replace(/,/g, ""));
    // }, [payableAmount]);

    const maxRefund = useMemo(() => {
        return Number(amountPaid || 0);
    }, [amountPaid]);

    const schema = yup.object().shape({
    refund_amount: yup
        .number()
        .transform((value, originalValue) =>
            originalValue === "" ? null : value
        )
        .typeError(t('refund_amount_required'))
        .required(t('refund_amount_required'))
        .min(0, t('refund_amount_min_0'))
        .max(maxRefund, t('refund_amount_max', { maxRefund })),

        remark: yup
            .string()
            .required(t('credit_debit_note_required'))
            .min(2, t('credit_debit_note_min_length'))
            .max(255, t('credit_debit_note_max_length')),
    });

    const {
        register,
        handleSubmit,
        formState: { errors },
        reset,
        setValue,
    } = useForm({
        resolver: yupResolver(schema),
        defaultValues: {
            refund_amount: maxRefund,
        },
    });

    /*
    FETCH ORDERS WHEN ADJUST SELECTED
    */

    useEffect(() => {
        if (refundType === "managed" && userId) {
            dispatch(fetchPendingOrders({ user_id: userId }));
        }
    }, [refundType, userId, dispatch]);

    /*
    RESET MODAL
    */

    useEffect(() => {
        if (!isOpen) {
            reset({ refund_amount: maxRefund });
            setRefundType("paid");
            setAdjustments([]);
        }
    }, [isOpen, reset, maxRefund]);

    /*
    LIVE CALCULATION
    */

    const totalAdjusted = adjustments.reduce(
        (sum, item) => sum + (item.amount || 0),
        0
    );

    const remainingRefund = Math.max(maxRefund - totalAdjusted, 0);

    useEffect(() => {
        if (refundType === "managed") {
            setValue("refund_amount", remainingRefund);
        } else {
            setValue("refund_amount", maxRefund);
        }
    }, [remainingRefund, refundType, maxRefund, setValue]);

    /*
    HANDLE ADJUSTMENT
    */

    // const handleAdjustmentChange = (orderId, value) => {
    //     const amount = Number(value);

    //     const order = pending_orders.find(o => o.id === orderId);
    //     const due = Number(order?.rfq?.payment_request?.due_amount) || 0;

    //     const finalAmount = Math.min(Math.max(amount, 0), due);

    //     setAdjustments((prev) => {
    //         const existing = prev.find((a) => a.order_id === orderId);

    //         if (existing) {
    //             return prev.map((a) =>
    //                 a.order_id === orderId
    //                     ? { ...a, amount: finalAmount }
    //                     : a
    //             );
    //         }

    //         return [...prev, { order_id: orderId, amount: finalAmount }];
    //     });
    // };  

    const handleAdjustmentChange = (orderId, value) => {
        const amount = Number(value) || 0;

        const order = pending_orders.find(o => o.id === orderId);
        const due = Number(order?.rfq?.payment_request?.due_amount) || 0;

        const finalAmount = Math.min(Math.max(amount, 0), due);

        // Clone adjustments
        let updatedAdjustments = [...adjustments];
        const existingIndex = updatedAdjustments.findIndex(
            (a) => a.order_id === orderId
        );

        if (existingIndex > -1) {
            updatedAdjustments[existingIndex].amount = finalAmount;
        } else {
            updatedAdjustments.push({ order_id: orderId, amount: finalAmount });
        }

        // Calculate total
        const total = updatedAdjustments.reduce(
            (sum, item) => sum + item.amount,
            0
        );

        if (total > amountPaid) {
            setAdjustmentErrors((prev) => ({
                ...prev,
                [orderId]: t('total_adjustment_exceed_error', { amountPaid }),
            }));
            return;
        } else {
            setAdjustmentErrors((prev) => {
                const newErrors = { ...prev };
                delete newErrors[orderId];
                return newErrors;
            });
        }

        setAdjustments(updatedAdjustments);
    };

    const hasErrors = Object.keys(adjustmentErrors).length > 0;

    /*
    SUBMIT
    */

    const submitHandler = (data) => {

        const payload = {
            ...data,
            type: refundType,
        };

        if (refundType === "managed") {
            payload.adjustments = adjustments.filter((a) => a.amount > 0);
        }

        onConfirm(payload);
    };

    const [currentRemainingRefund, setCurrentRemainingRefund] = useState(remainingRefund);
    const calculateRemainingAmount = (event) => {
        let value = event.target.value;
        setCurrentRemainingRefund(remainingRefund - value);
    }

    return (

        <Dialog
            open={isOpen}
            handler={onClose}
            size="md"
            className="max-w-lg max-h-[90vh] overflow-y-auto"
        >

            {loading && <ShowLoader />}

            <DialogHeader className="flex justify-between items-center border-b pb-3">

                <Typography variant="h5">
                    {t('approve_refund_title')}
                </Typography>

                <XMarkIcon
                    className="h-5 w-5 cursor-pointer"
                    onClick={onClose}
                />

            </DialogHeader>

            <form onSubmit={handleSubmit(submitHandler)}>

                <DialogBody className={`${refundType === "managed" ? "min-h-[520px]" : "min-h-[380px]"}`}>

                    {/* Refund Type */}

                    <div className="mb-6">

                        <label className="text-sm font-semibold">
                            {t('refund_type_label')}
                        </label>

                        <div className="flex gap-6 mt-2">

                            <label className="flex items-center gap-2 cursor-pointer">
                                <input
                                    type="radio"
                                    checked={refundType === "paid"}
                                    onChange={() => setRefundType("paid")}
                                />
                                {t('refund_radio_label')}
                            </label>

                            <label className="flex items-center gap-2 cursor-pointer">
                                <input
                                    type="radio"
                                    checked={refundType === "managed"}
                                    onChange={() => setRefundType("managed")}
                                />
                                {t('adjust_radio_label')}
                            </label>

                        </div>

                    </div>

                    {/* Refund Summary (ONLY IN ADJUST TAB) */}

                    {refundType === "managed" && (

                        <div className="bg-gray-50 border rounded-lg p-3 mb-6 text-sm">

                            <div className="flex justify-between">
                                <span>{t('total_order_amount_label')}</span>
                                <span className="font-semibold">
                                    {t('sar_currency_label')} {payableAmount}
                                </span>
                            </div>

                            <div className="flex justify-between mt-1">
                                <span>{t('amount_paid_label')}</span>
                                <span className="text-purple-600 font-semibold">
                                    {t('sar_currency_label')} {amountPaid}
                                </span>
                            </div>

                            <div className="flex justify-between mt-1">
                                <span>{t('adjusted_to_orders_label')}</span>
                                <span className="text-blue-600 font-semibold">
                                    {t('sar_currency_label')} {totalAdjusted}
                                </span>
                            </div>

                            <div className="flex justify-between mt-1">
                                <span>{t('remaining_refund_label')}</span>
                                <span className="text-green-600 font-semibold">
                                    {t('sar_currency_label')} {currentRemainingRefund}
                                </span>
                            </div>

                        </div>

                    )}

                    {/* Adjust Orders */}

                    {refundType === "managed" && (

                        <div className="mb-6">

                            <label className="text-sm font-semibold mb-2 block">
                                {t('adjust_refund_to_orders_label')}
                            </label>

                            <div className="border rounded-lg p-3 max-h-[200px] overflow-y-auto">

                                {Array.isArray(pending_orders) &&
                                pending_orders.length > 0 ? (

                                    pending_orders.map((order) => {

                                        {/* const due =
                                            order?.rfq?.payment_request?.due_amount || 0; */}
                                        const due = parseFloat(order?.rfq?.payment_request?.due_amount) || 0;

                                        return (

                                            <div
                                                key={order.id}
                                                className="flex items-center gap-3 mb-2"
                                            >

                                                <div className="flex-1 text-sm">

                                                    {order.order_id}

                                                    <span className="text-gray-500 ml-2">
                                                        ({t('due_label')}: {t('sar_currency_label')} {due})
                                                    </span>

                                                </div>
                                                <div>
                                                <input
                                                    type="number"
                                                    placeholder={t('amount_placeholder')}
                                                    className="w-28 border rounded px-2 py-1 text-sm"
                                                    min="0"
                                                    value={
                                                        adjustments.find(a => a.order_id === order.id)?.amount || ""
                                                    }
                                                    onChange={(e) =>
                                                        handleAdjustmentChange(order.id, e.target.value)
                                                    }
                                                />

                                                {adjustmentErrors[order.id] && (
                                                    <p className="text-red-500 text-xs mt-1">
                                                        {adjustmentErrors[order.id]}
                                                    </p>
                                                )}
                                            </div>

                                             {/* <input
                                                type="number"
                                                placeholder="Amount"
                                                className="w-28 border rounded px-2 py-1 text-sm"
                                                max={amountPaid}
                                                min="0"
                                                value={
                                                    adjustments.find(a => a.order_id === order.id)?.amount || ""
                                                }
                                                onChange={(e) =>
                                                    handleAdjustmentChange(order.id, e.target.value)
                                                }
                                                /> */}

                                            </div>

                                        );
                                    })

                                ) : (

                                    <p className="text-sm text-gray-500">
                                        {t('no_pending_orders')}
                                    </p>

                                )}

                            </div>

                        </div>

                    )}

                    {/* Refund Amount */}

                    <div className="mb-5">

                        <div className="flex items-center justify-between">
                            <label className="text-sm font-semibold">
                                {t('refund_amount_to_customer_label')}
                            </label>

                            <span className="text-xs italic text-gray-500 mr-3">
                                {t('max_label')} : {t('sar_currency_label')} {amountPaid}
                            </span>
                        </div>

                        <input
                            type="number"
                            className="w-full border rounded-lg px-3 py-2 mt-1 bg-gray-100"
                            {...register("refund_amount")}
                            onChange={(e) => calculateRemainingAmount(e)}
                        />

                        {errors.refund_amount && (
                            <p className="text-red-500 text-xs">
                                {errors.refund_amount.message}
                            </p>
                        )}

                    </div>

                    {/* Remark */}

                    <div>

                        <label className="text-sm font-semibold">
                            {t('credit_debit_notes_label')}
                        </label>

                        <textarea
                            rows={3}
                            className="w-full border rounded-lg px-3 py-2 mt-1"
                            {...register("remark")}
                        />

                        {errors.remark && (
                            <p className="text-red-500 text-xs">
                                {errors.remark.message}
                            </p>
                        )}

                    </div>

                </DialogBody>

                <DialogFooter className="border-t pt-3 flex gap-3">

                    <button
                        type="button"
                        onClick={onClose}
                        className="px-4 py-2 bg-gray-200 rounded"
                    >
                        {t('cancel_button')}
                    </button>

                    <button
                        type="submit"
                        disabled={loading || hasErrors}
                        className="px-4 py-2 bg-green-600 text-white rounded"
                    >
                        {loading ? t('processing_button') : t('approve_button')}
                    </button>

                </DialogFooter>

            </form>

        </Dialog>
    );
}

export default RefundApprovalModal;